import { useNavigate } from 'react-router-dom';

/**
 * Performs a smooth page transition animation between pages
 * This creates a visual effect where the page slides up and then down
 * 
 * @param navigateFunction - React Router's navigate function
 * @param navigateTo - The path to navigate to
 * @param duration - Animation duration in milliseconds (default: 1200)
 */
export const smoothPageTransition = (
  navigateFunction: ReturnType<typeof useNavigate>,
  navigateTo: string,
  duration: number = 1200
): void => {
  // Create transition overlay div
  const overlay = document.createElement('div');
  overlay.className = 'page-transition-overlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.zIndex = '9999';
  overlay.style.pointerEvents = 'none';
  overlay.style.backgroundColor = 'transparent';
  document.body.appendChild(overlay);
  
  // Get the main content container
  const mainContent = document.querySelector('main') || document.querySelector('.content-wrapper') || document.body;
  
  // Step 1: Animate current page sliding up
  mainContent.style.transition = `transform ${duration/2}ms cubic-bezier(0.25, 0.1, 0.25, 1.0)`;
  mainContent.style.transform = 'translateY(-70px)';
  mainContent.style.opacity = '0.7';
  
  // After first animation completes
  setTimeout(() => {
    // Step 2: Navigate to the new page
    navigateFunction(navigateTo);
    
    // Step 3: After navigation, prepare and animate the new page
    setTimeout(() => {
      // Find the new main content
      const newMainContent = document.querySelector('main') || document.querySelector('.content-wrapper') || document.body;
      
      // Set initial position (above viewport)
      newMainContent.style.transition = 'none';
      newMainContent.style.transform = 'translateY(-100px)';
      newMainContent.style.opacity = '0.4';
      
      // Force browser to recognize these styles before the animation
      newMainContent.offsetHeight;
      
      // Now animate sliding down into view (with slow start and end for smoothness)
      newMainContent.style.transition = `transform ${duration/2}ms cubic-bezier(0.25, 0.1, 0.25, 1.0), opacity ${duration/2}ms ease-out`;
      newMainContent.style.transform = 'translateY(0)';
      newMainContent.style.opacity = '1';
      
      // Clean up the overlay after transition completes
      setTimeout(() => {
        if (document.body.contains(overlay)) {
          document.body.removeChild(overlay);
        }
      }, duration/2 + 100);
    }, 150); // Even longer delay to ensure the new page has rendered
  }, duration/2);
};
